/**
 * QR Code Login component — SingPass-style cross-device authentication.
 *
 * Displays a QR code that a mobile app can scan to approve a web login.
 * Polls the gateway for status changes and handles the full lifecycle:
 *   pending → scanned (shows match number) → approved (session set) → redirect
 *
 * Usage: Rendered as a tab/option on the Login page.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { PENDING_TOTP_KEY } from '../lib/webauthn';
import QRCode from './QRCode';
import LoadingSpinner from './LoadingSpinner';

import { config } from '../config';
const GATEWAY_URL = config.gatewayUrl;
const POLL_INTERVAL_MS = 2000;

type QRLoginStatus = 'idle' | 'pending' | 'scanned' | 'approved' | 'denied' | 'expired' | 'error';

interface QRLoginProps {
  /** Called when login is approved and session cookie is set */
  onSuccess: (userEmail?: string) => void;
  /** Return URL after successful login */
  returnTo?: string;
}

export default function QRLogin({ onSuccess, returnTo }: QRLoginProps) {
  const [status, setStatus] = useState<QRLoginStatus>('idle');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [matchNumber, setMatchNumber] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Initiate QR challenge ──
  const initiate = useCallback(async () => {
    setStatus('idle');
    setError(null);
    setMatchNumber(null);
    setUserEmail(null);

    try {
      const resp = await fetch(`${GATEWAY_URL}/v1/auth/qr-login/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!resp.ok) {
        throw new Error(`Failed to create QR challenge: ${resp.status}`);
      }

      const data = await resp.json();
      setChallengeId(data.challenge_id);
      setQrData(data.qr_data);
      setExpiresAt(new Date(data.expires_at));
      setStatus('pending');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create QR code');
      setStatus('error');
    }
  }, []);

  // ── Poll for status ──
  const pollStatus = useCallback(async () => {
    if (!challengeId) return;

    try {
      const resp = await fetch(`${GATEWAY_URL}/v1/auth/qr-login/${challengeId}/status`, {
        credentials: 'include', // Important: receives Set-Cookie from gateway
      });

      if (!resp.ok) {
        if (resp.status === 404) {
          setStatus('expired');
          return;
        }
        return; // Transient error, keep polling
      }

      const data = await resp.json();

      if (data.status === 'scanned' && data.match_number) {
        setMatchNumber(data.match_number);
        setStatus('scanned');
      } else if (data.status === 'approved' && data.redirect_url) {
        // Kratos recovery link — navigate there to create a real session.
        // Store return destination so we can redirect after session is created.
        sessionStorage.setItem('qr_login_return_to', returnTo || '/organizations');
        // Accounts with 2FA: the app sent its authenticator code with the approval.
        // Login.tsx submits it to the second step after the recovery redirect (OPP-144).
        if (data.totp_code) {
          sessionStorage.setItem(PENDING_TOTP_KEY, JSON.stringify({ code: data.totp_code, at: Date.now() }));
        }
        window.location.href = data.redirect_url;
        setStatus('approved');
        return;
      } else if (data.status === 'denied') {
        setStatus('denied');
      } else if (data.status === 'expired') {
        setStatus('expired');
      }
    } catch {
      // Network error — keep polling
    }
  }, [challengeId]);

  // ── Start polling when pending or scanned ──
  useEffect(() => {
    if (status === 'pending' || status === 'scanned') {
      pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
    // Stop polling for terminal states
    if (pollRef.current) clearInterval(pollRef.current);
  }, [status, pollStatus]);

  // ── Countdown timer ──
  useEffect(() => {
    if (!expiresAt || (status !== 'pending' && status !== 'scanned')) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        setStatus('expired');
      }
    };

    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [expiresAt, status]);

  // ── Handle approved ──
  useEffect(() => {
    if (status === 'approved') {
      // Small delay for visual feedback
      const timer = setTimeout(() => onSuccess(userEmail || undefined), 1000);
      return () => clearTimeout(timer);
    }
  }, [status, onSuccess, userEmail]);

  // ── Auto-start on mount ──
  useEffect(() => {
    initiate();
  }, [initiate]);

  // ── Cleanup ──
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── Approved state ──
  if (status === 'approved') {
    return (
      <div className="flex flex-col items-center py-8 space-y-4">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-content-primary">Login Approved</h3>
        {userEmail && (
          <p className="text-sm text-content-secondary">Signed in as {userEmail}</p>
        )}
        <LoadingSpinner text="Redirecting..." size="sm" />
      </div>
    );
  }

  // ── Denied state ──
  if (status === 'denied') {
    return (
      <div className="flex flex-col items-center py-8 space-y-4">
        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-content-primary">Login Denied</h3>
        <p className="text-sm text-content-secondary">The request was denied from your mobile device.</p>
        <button onClick={initiate} className="btn-primary">Try Again</button>
      </div>
    );
  }

  // ── Expired state ──
  if (status === 'expired') {
    return (
      <div className="flex flex-col items-center py-8 space-y-4">
        <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-content-primary">QR Code Expired</h3>
        <p className="text-sm text-content-secondary">The QR code has expired. Generate a new one.</p>
        <button onClick={initiate} className="btn-primary">Generate New QR Code</button>
      </div>
    );
  }

  // ── Error state ──
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center py-8 space-y-4">
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={initiate} className="btn-primary">Try Again</button>
      </div>
    );
  }

  // ── Scanned state — show match number ──
  if (status === 'scanned' && matchNumber) {
    return (
      <div className="flex flex-col items-center py-8 space-y-4">
        <div className="w-16 h-16 rounded-full bg-orbital-100 dark:bg-orbital-900 flex items-center justify-center">
          <svg className="w-8 h-8 text-orbital-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>

        <h3 className="text-lg font-semibold text-content-primary">Confirm on your phone</h3>
        <p className="text-sm text-content-secondary">
          Verify this number matches your mobile device
        </p>

        <div className="text-5xl font-mono font-bold text-orbital-600 tracking-widest py-4">
          {matchNumber}
        </div>

        <p className="text-xs text-content-tertiary">
          Waiting for approval... {formatTime(timeLeft)}
        </p>
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  // ── Pending / Loading state — show QR code ──
  return (
    <div className="flex flex-col items-center py-4 space-y-3">
      {status === 'idle' ? (
        <LoadingSpinner text="Generating QR code..." />
      ) : qrData ? (
        <>
          <p className="text-sm text-content-secondary text-center">
            Scan with the Orbital app to sign in
          </p>

          <QRCode
            value={qrData}
            size={250}
            level="H"
            downloadable={false}
            copyable={false}
          />

          <p className="text-xs text-content-tertiary">
            Expires in {formatTime(timeLeft)}
          </p>

          <button
            onClick={initiate}
            className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300"
          >
            Generate new code
          </button>
        </>
      ) : null}
    </div>
  );
}
